import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { hasAccessToEditProfileSelector } from '../../CommonSelectors/CommonAreaSelector';
import { useOtherAssetsQuery } from './api/useOtherAssetsQuery';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { RealEstate } from './tables/RealEstate/RealEstate';
import { Business } from './tables/Business/Business';
import { Pensions } from './tables/Pensions/Pensions';
import { MiscAssets } from './tables/MiscAssets/MiscAssets';
import { DeferredCompensation } from './tables/DeferredCompensation/DeferredCompensation';
import { UpsertAssetModal } from './components/UpsertAssetModal/UpsertAssetModal';
import { DeleteAssetModal } from './components/DeleteAssetModal/DeleteAssetModal';
import { AssetToUpsert } from './components/UpsertAssetModal/UpsertAssetModal.meta';
import { AssetToDelete } from './components/DeleteAssetModal/DeleteAssetModal.meta';
import { OtherAssetType } from './OtherAssets.meta';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { RealEstateForm } from './forms/RealEstate/RealEstateForm';
import { RealEstateDTO } from './tables/RealEstate/RealEstate.meta';
import { BusinessAssetsForm } from './forms/BusinessAssets/BusinessAssetsForm';
import { BusinessAssetDTO } from './tables/Business/Business.meta';
import { PensionForm } from './forms/Pension/PensionForm';
import { PensionDTO } from './tables/Pensions/Pensions.meta';
import { MiscAssetsForm } from './forms/MiscAssets/MiscAssetsForm';
import { MiscAssetDTO } from './tables/MiscAssets/MiscAssets.meta';
import { DeferredCompensationForm } from './forms/DeferredCompensation/DeferredCompensationForm';
import { DeferredCompensationDTO } from './tables/DeferredCompensation/DeferredCompensation.meta';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfileTablesLoading } from '../components/ProfileTablesLoading/ProfileTablesLoading';
import { WorkflowIdEnum } from '../../Workflow/api/useWorkflowDataQuery';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import {
  dataToForm as realEstateDataToForm,
  formToBody as realEstateFormToBody,
  UpsertRealEstateDTO,
} from './forms/RealEstate/RealEstateForm.meta';
import {
  dataToForm as businessDataToForm,
  formToBody as businessFormToBody,
  UpsertBusinessAssetDTO,
} from './forms/BusinessAssets/BusinessAssetsForm.meta';
import {
  dataToForm as pensionDataToForm,
  formToBody as pensionFormToBody,
  UpsertPensionDTO,
} from './forms/Pension/PensionForm.meta';
import {
  dataToForm as miscDataToForm,
  formToBody as miscFormToBody,
  UpsertMiscAssetsDTO,
} from './forms/MiscAssets/MiscAssetsForm.meta';
import { SaveDeferredCompensationDTO } from './forms/DeferredCompensation/DeferredCompensationForm.meta';
import { useUpsertRealEstateMutation } from './api/useUpsertRealEstateMutation';
import { useUpsertBusinessAssetsMutation } from './api/useUpsertBusinessAssetsMutation';
import { useUpsertPensionMutation } from './api/useUpsertPensionMutation';
import { useUpsertMiscAssetsMutation } from './api/useUpsertMiscAssetsMutation';
import { useSaveDeferredCompensationMutation } from './api/useSaveDeferredCompensationMutation';

export interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const OtherAssets = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const canEdit = useSelector(hasAccessToEditProfileSelector);
  const { isSuccess } = useOtherAssetsQuery({ clientId });

  const [assetToUpsert, setAssetToUpsert] = useState<AssetToUpsert | null>(null);

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Other Assets"
          description="Please include pensions, real estate, business assets, and other miscellaneous assets."
        >
          {isSuccess && canEdit && (
            <Button leftIcon="fas fa-plus" onClick={() => setAssetToUpsert({})}>
              Add Asset
            </Button>
          )}
        </ProfilePageHeader>

        <OtherAssetsContent
          clientId={clientId}
          workflowProps={workflowProps}
          assetToUpsert={assetToUpsert}
          setAssetToUpsert={setAssetToUpsert}
        />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};

interface OtherAssetsContentProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  assetToUpsert: AssetToUpsert | null;
  setAssetToUpsert: (assetToUpsert: AssetToUpsert | null) => void;
}

const OtherAssetsContent = ({
  clientId,
  workflowProps,
  assetToUpsert,
  setAssetToUpsert,
}: OtherAssetsContentProps) => {
  const canEdit = useSelector(hasAccessToEditProfileSelector);
  const [assetToDelete, setAssetToDelete] = useState<AssetToDelete | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const { isLoading, isError, refetch, data } = useOtherAssetsQuery({ clientId });

  const { mutateAsync: upsertRealEstate } = useUpsertRealEstateMutation({ clientId });
  const { mutateAsync: upsertBusinessAsset } = useUpsertBusinessAssetsMutation({ clientId });
  const { mutateAsync: upsertPension } = useUpsertPensionMutation({ clientId });
  const { mutateAsync: upsertMiscAsset } = useUpsertMiscAssetsMutation({ clientId });
  const { mutateAsync: upsertDeferredCompensation } = useSaveDeferredCompensationMutation({
    clientId,
  });

  const isEstatePlanningWorkflow = workflowProps?.workflowId === WorkflowIdEnum.EstatePlanning;

  useEffect(() => {
    setIsFormDirty(false);
  }, [assetToUpsert]);

  const handleFormDirtyChange = useCallback((dirty: boolean) => {
    setIsFormDirty(prev => (prev === dirty ? prev : dirty));
  }, []);

  if (isLoading) {
    return <ProfileTablesLoading isWorkflow={!!workflowProps} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    return null;
  }

  const handleClone = async (asset: AssetToUpsert) => {
    const assetType = asset?.OtherAssetType;

    if (assetType === undefined || assetType === null) return;

    const typedAssetType: OtherAssetType = assetType;
    const name = asset.Name
      ? `Cloned - ${asset.Name}`
      : asset.OtherAssetType
      ? `Cloned - ${OtherAssetType[asset.OtherAssetType]}`
      : 'Cloned - Asset';
    try {
      switch (typedAssetType) {
        case OtherAssetType.RealEstate: {
          const realAsset = asset as RealEstateDTO;
          const formValues = realEstateDataToForm({
            data: asset as RealEstateDTO,
            options: data.Options,
          });

          const body: UpsertRealEstateDTO = {
            clientId,
            ...realEstateFormToBody(formValues),
            Id: null,
            Name: name,
            MortgageId: null,
            MortgageInstitutionId: realAsset.MortgageInstitutionId,
            MortgageInstitutionName: realAsset.MortgageInstitutionName,
          };

          await upsertRealEstate(body);
          break;
        }

        case OtherAssetType.Business: {
          const formValues = businessDataToForm({
            data: asset as BusinessAssetDTO,
            options: data.Options,
          });

          const body: UpsertBusinessAssetDTO = {
            clientId,
            ...businessFormToBody(formValues),
            Name: name,
            Id: null,
          };

          await upsertBusinessAsset(body);
          break;
        }

        case OtherAssetType.Pension: {
          const formValues = pensionDataToForm({
            data: asset as PensionDTO,
            options: data.Options,
          });

          const body: UpsertPensionDTO = {
            clientId,
            ...pensionFormToBody(formValues),
            Id: null,
            Name: name,
          };

          await upsertPension(body);
          break;
        }

        case OtherAssetType.Miscellaneous: {
          const formValues = miscDataToForm({
            data: asset as MiscAssetDTO,
            options: data.Options,
          });

          const body: UpsertMiscAssetsDTO = {
            clientId,
            ...miscFormToBody(formValues),
            Id: null,
            Name: name,
          };

          await upsertMiscAsset(body);
          break;
        }

        case OtherAssetType.DeferredCompensation: {
          const deferredAsset = asset as DeferredCompensationDTO;

          const body: SaveDeferredCompensationDTO = {
            ...deferredAsset,
            ClientId: clientId,
            Id: null,
            Name: name,
            CustomDistributionSchedule: deferredAsset.CustomDistributionSchedule ?? [],
          };

          await upsertDeferredCompensation(body);
          break;
        }

        default:
          return;
      }

      toast.success('Asset cloned successfully');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gridGap="32" data-testid="other-assets--content">
        <RealEstate
          data={data.Assets.RealEstates}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
          onClone={canEdit ? handleClone : undefined}
        />

        <Business
          data={data.Assets.BusinessAssets}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
          onClone={canEdit ? handleClone : undefined}
        />

        <Pensions
          data={data.Assets.Pensions}
          options={data.Options.PensionOptions}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
          onClone={canEdit ? handleClone : undefined}
        />

        <MiscAssets
          data={data.Assets.MiscellaneousAssets}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
          onClone={canEdit ? handleClone : undefined}
        />

        <DeferredCompensation
          data={data.Assets.DeferredCompensation}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
          onClone={canEdit ? handleClone : undefined}
        />
      </Box>

      {assetToUpsert && (
        <UpsertAssetModal
          asset={assetToUpsert}
          onClose={() => setAssetToUpsert(null)}
          onAssetTypeChange={assetType => setAssetToUpsert({ OtherAssetType: assetType })}
          assetToDelete={assetToDelete}
          setAssetToDelete={setAssetToDelete}
          onClone={() => {
            const name = assetToUpsert.Name
              ? `Cloned - ${assetToUpsert.Name}`
              : assetToUpsert.OtherAssetType
              ? `Cloned - ${OtherAssetType[assetToUpsert.OtherAssetType]}`
              : 'Cloned - Asset';
            setAssetToUpsert({ ...assetToUpsert, isClone: true, Name: name });
            setIsFormDirty(false);
          }}
          isCloneDisabled={isFormDirty}
        >
          {assetToUpsert.OtherAssetType === OtherAssetType.RealEstate && (
            <RealEstateForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as RealEstateDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
              onDirtyChange={handleFormDirtyChange}
              readOnly={!canEdit}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.Business && (
            <BusinessAssetsForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as BusinessAssetDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
              onDirtyChange={handleFormDirtyChange}
              readOnly={!canEdit}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.Pension && (
            <PensionForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as PensionDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
              onDirtyChange={handleFormDirtyChange}
              readOnly={!canEdit}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.Miscellaneous && (
            <MiscAssetsForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as MiscAssetDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
              onDirtyChange={handleFormDirtyChange}
              readOnly={!canEdit}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.DeferredCompensation && (
            <DeferredCompensationForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as DeferredCompensationDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
              onDirtyChange={handleFormDirtyChange}
              readOnly={!canEdit}
            />
          )}
        </UpsertAssetModal>
      )}

      {assetToDelete && (
        <DeleteAssetModal
          clientId={clientId}
          asset={assetToDelete}
          onClose={() => {
            setAssetToDelete(null);
          }}
          onDelete={() => {
            setAssetToDelete(null);
            setAssetToUpsert(null);
          }}
        />
      )}
    </>
  );
};
